import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { quotationKeys, invoiceKeys } from '@/lib/query-keys';
import { quotationService, type CreateQuotationInvoicePayload } from '@/services/quotation.service';
import { useQuotationFilterStore } from '@/stores/quotationFilterStore';

function apiMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || fallback;
}

export function useQuotations() {
  const queryClient = useQueryClient();
  const filters = useQuotationFilterStore();

  const quotationsQuery = useQuery({
    queryKey: quotationKeys.list(filters.current),
    queryFn: () => quotationService.getAll(filters.current),
    placeholderData: keepPreviousData,
  });

  const createQuotation = useMutation({
    mutationFn: (data: Record<string, unknown>) => quotationService.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: quotationKeys.lists() }); toast.success('Quotation created'); },
    onError: () => toast.error('Failed to create quotation'),
  });

  const updateQuotation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => quotationService.update(id, data),
    onSuccess: (_, { id }) => { queryClient.invalidateQueries({ queryKey: quotationKeys.lists() }); queryClient.invalidateQueries({ queryKey: quotationKeys.detail(id) }); toast.success('Quotation updated'); },
    onError: () => toast.error('Failed to update quotation'),
  });

  const deleteQuotation = useMutation({
    mutationFn: (id: string) => quotationService.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: quotationKeys.lists() }); toast.success('Quotation deleted'); },
    onError: (error) => toast.error(apiMessage(error, 'Failed to delete quotation')),
  });

  const sendQuotation = useMutation({
    mutationFn: (id: string) => quotationService.send(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: quotationKeys.all }); toast.success('Quotation sent'); },
    onError: () => toast.error('Failed to send quotation'),
  });

  const acceptQuotation = useMutation({
    mutationFn: (id: string) => quotationService.accept(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quotationKeys.all });
      toast.success('Quotation accepted');
    },
    onError: (error) => toast.error(apiMessage(error, 'Failed to accept quotation')),
  });

  const createInvoiceFromQuotation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateQuotationInvoicePayload }) => quotationService.createInvoice(id, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: quotationKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      toast.success(`Draft invoice ${res?.data?.invoiceNumber ?? ''} created`.trim());
    },
    // The server explains a refusal (over the quotation total, nothing picked…).
    onError: (error) => toast.error(apiMessage(error, 'Failed to create the invoice')),
  });

  const rejectQuotation = useMutation({
    mutationFn: (id: string) => quotationService.reject(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: quotationKeys.all }); toast.success('Quotation rejected'); },
    onError: () => toast.error('Failed to reject quotation'),
  });

  const cancelQuotation = useMutation({
    mutationFn: (id: string) => quotationService.cancel(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: quotationKeys.all }); toast.success('Quotation cancelled'); },
    onError: () => toast.error('Failed to cancel quotation'),
  });

  const duplicateQuotation = useMutation({
    mutationFn: (id: string) => quotationService.duplicate(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: quotationKeys.lists() }); toast.success('Quotation duplicated'); },
    onError: () => toast.error('Failed to duplicate quotation'),
  });

  return {
    quotations: quotationsQuery.data?.data ?? [],
    meta: quotationsQuery.data?.meta,
    isLoading: quotationsQuery.isLoading,
    filters,
    createQuotation, updateQuotation, deleteQuotation,
    sendQuotation, acceptQuotation, rejectQuotation, cancelQuotation, duplicateQuotation, createInvoiceFromQuotation,
    downloadPdf: (id: string) => quotationService.downloadPdf(id),
  };
}

export function useQuotation(id: string) {
  const q = useQuery({ queryKey: quotationKeys.detail(id), queryFn: () => quotationService.getById(id), enabled: !!id });
  return { quotation: q.data?.data, isLoading: q.isLoading };
}
